import { memo, useState } from 'react';
import { useMedia } from '../../media.context';
import {
  Button,
  Dialog,
  Hint,
  HStack,
  message,
  Popconfirm,
  Text,
  VStack,
} from 'components/ui';
import { ArrowWrapper, StickyContainer, StyledPanel } from './styles';
import { SVG } from 'components/svg';
import { Icons } from 'assets';
import { useFileDownload } from 'hooks';
import { Carousel } from './carousel';
import { truncateString } from 'utils';
import { MediaThumbnailsProps } from '../media.thumbnails';
import { ThumbnailCarousel } from './thumbnail-carousel';

type MediaPreviewerProps = Pick<MediaThumbnailsProps, 'canDelete'>;

export const MediaPreviewer = memo(({ canDelete }: MediaPreviewerProps) => {
  const { files, dispatch, previewer } = useMedia();
  const { download, isLoading } = useFileDownload();
  const file = files[previewer.openedIndex ?? 0];
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleDownload = async () => {
    try {
      if (!file.url) {
        throw new Error();
      }

      await download({
        url: file.url,
        filename: file.name,
      });
      message({ type: 'success', text: 'Файл скачан' });
    } catch {
      message({ type: 'error', text: 'Упс... Произошла ошибка' });
    }
  };

  const handleDeleteFile = () => {
    const newFiles = dispatch({
      type: 'MEDIA/FILE_REMOVED',
      file,
    });

    message({ type: 'success', text: 'Файл удалён' });
    setIsOpen(false);

    if (newFiles.length) {
      previewer.goTo(Math.max((previewer.openedIndex ?? 0) - 1, 0));
    } else {
      previewer.close();
    }
  };

  return (
    <Dialog.Root open={previewer.isOpen} onOpenChange={previewer.toggle}>
      <Dialog.Content withScrollArrows={false}>
        <Dialog.Title hidden>Просмотр медиа файлов</Dialog.Title>
        <Dialog.Description hidden>
          Компонент для просмотра медиа файлов
        </Dialog.Description>

        <VStack align="center">
          <StyledPanel>
            <ArrowWrapper
              className="left"
              onClick={previewer.prev}
              disabled={previewer.openedIndex === 0}
            >
              <SVG src={Icons.Chevron} props={{ size: '24px', deg: 90 }} />
            </ArrowWrapper>

            <Carousel />

            <ArrowWrapper
              className="right"
              onClick={previewer.next}
              disabled={(previewer.openedIndex ?? 0) + 1 === files.length}
            >
              <SVG src={Icons.Chevron} props={{ size: '24px', deg: -90 }} />
            </ArrowWrapper>
          </StyledPanel>

          <StickyContainer>
            <ThumbnailCarousel />

            <Dialog.Panel
              style={{
                width: 'fit-content',
                marginBottom: 0,
              }}
            >
              <HStack padding={16} align="center" spacing="l">
                <Text>
                  {(previewer.openedIndex ?? 0) + 1} из {files.length}
                </Text>
                <HStack flex={1}>
                  <Hint content={file.name}>
                    <Text>{truncateString(file.name, 32, 8, 8)}</Text>
                  </Hint>
                </HStack>

                <HStack align="center" spacing="xs">
                  <Button
                    mode="accent"
                    size="s"
                    pending={isLoading}
                    onClick={handleDownload}
                  >
                    Скачать
                  </Button>

                  {canDelete && (
                    <Popconfirm
                      side="right"
                      title="Удаление файла"
                      description="Вы уверены, что хотите удалить файл?"
                      confirmText="Удалить"
                      open={isOpen}
                      onOpenChange={setIsOpen}
                      onConfirm={handleDeleteFile}
                    >
                      <Button mode="error" size="s">
                        Удалить
                      </Button>
                    </Popconfirm>
                  )}
                </HStack>
              </HStack>
            </Dialog.Panel>
          </StickyContainer>
        </VStack>
      </Dialog.Content>
    </Dialog.Root>
  );
});
